import { Fragment, type ReactElement } from 'react';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBreakdownLine } from '@/features/shared/components/tableSystem/TableBreakdownLine';
import { TableBreakdownStack } from '@/features/shared/components/tableSystem/TableMetaCells';
import { resolveCiStatusLabelKey } from '@/features/shared/utils/ciStatus';
import { SummaryTooltipContent } from '@/ui/composites/table/cells/summaryCellUtils';
import { Box, BuildsIcon, formatStatusLabel, Tooltip } from '@/ui/primitives';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { SvgIconComponent } from '@/ui/primitives';
import type { BuildStatus, CiDisplayStatusKey } from '@flowqa/shared';

type BuildBreakdownStatus = BuildStatus | CiDisplayStatusKey;

type TableBuildStatusBreakdownCellProps = {
  total: number;
  counts: Partial<Record<BuildBreakdownStatus, number>> | null | undefined;
  summaryItems?: SummaryTooltipItem[];
  totalLabelSingular?: string;
  totalLabelPlural?: string;
  showDetailLabel?: boolean;
  totalIcon?: SvgIconComponent;
  showDateHeaders?: boolean;
};

const BREAKDOWN_TOOLTIP_ENTER_DELAY_MS = 250;

const formatPercent = (count: number, total: number): string => {
  if (total <= 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
};

const getBuildStatusIconColor = (status: BuildBreakdownStatus): string => {
  if (status === 'completed' || status === 'success') return 'success.main';
  if (status === 'failed' || status === 'canceled' || status === 'timed_out') return 'error.main';
  if (status === 'running') return 'info.main';
  if (status === 'warning' || status === 'skipped' || status === 'action_required') {
    return 'warning.main';
  }
  return 'text.secondary';
};

const breakdownStatuses: BuildBreakdownStatus[] = [
  'completed',
  'success',
  'failed',
  'running',
  'canceled',
  'queued',
  'warning',
  'skipped',
  'action_required',
  'timed_out',
  'neutral',
  'unknown',
];

export const TableBuildStatusBreakdownCell = ({
  total,
  counts,
  summaryItems = [],
  totalLabelSingular = 'job',
  totalLabelPlural = 'jobs',
  showDetailLabel = false,
  totalIcon: TotalIcon = BuildsIcon,
  showDateHeaders = false,
}: TableBuildStatusBreakdownCellProps): ReactElement => {
  const visibleRows = breakdownStatuses.filter((status) => (counts?.[status] ?? 0) > 0);
  const renderTooltipRow = ({
    icon,
    content,
    items,
  }: {
    icon: ReactElement;
    content: string;
    items: SummaryTooltipItem[];
  }): ReactElement => (
    <Tooltip
      title={
        <SummaryTooltipContent
          items={items}
          linkMode="execution"
          skippedIconVariant="outlined"
          showDetailLabel={showDetailLabel}
          showDateHeaders={showDateHeaders}
        />
      }
      disableHoverListener={items.length === 0}
      enterDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      enterNextDelay={BREAKDOWN_TOOLTIP_ENTER_DELAY_MS}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 'none',
            width: 'fit-content',
            fontSize: '15px',
            lineHeight: 1.4,
            py: 0,
            pr: 0,
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{ display: 'inline-block', width: 'fit-content', maxWidth: '100%' }}
      >
        <TableBreakdownLine
          icon={icon}
          content={content}
          disableOverflowTooltip={items.length > 0}
        />
      </Box>
    </Tooltip>
  );

  return (
    <TableBreakdownStack>
      {renderTooltipRow({
        icon: (
          <TotalIcon
            sx={{
              fontSize: (theme) => theme.app.iconSizes.sm,
              flexShrink: 0,
              color: 'text.secondary',
              transform: 'translateY(2px)',
            }}
          />
        ),
        content: `${total.toLocaleString()} ${total === 1 ? totalLabelSingular : totalLabelPlural}`,
        items: summaryItems,
      })}
      {visibleRows.map((status) => {
        const count = counts?.[status] ?? 0;
        const StatusIcon = getBuildStatusIconComponent(status);
        const statusSummaryItems = summaryItems.filter((item) => item.status === status);
        const label = formatStatusLabel(resolveCiStatusLabelKey(status));
        return (
          <Fragment key={status}>
            {renderTooltipRow({
              icon: (
                <StatusIcon
                  sx={{
                    fontSize: (theme) => theme.app.iconSizes.sm,
                    flexShrink: 0,
                    color: getBuildStatusIconColor(status),
                    transform: 'translateY(2px)',
                  }}
                />
              ),
              content: `${count.toLocaleString()} ${label.toLowerCase()} (${formatPercent(count, total)})`,
              items: statusSummaryItems,
            })}
          </Fragment>
        );
      })}
    </TableBreakdownStack>
  );
};
